import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { CheckCircle2, XCircle, Loader2, Crosshair as CrosshairIcon } from "lucide-react";

export default function VerifyEmail() {
  const [sp] = useSearchParams();
  const { refresh } = useAuth();
  const [state, setState] = useState("loading"); // loading | ok | error
  const [message, setMessage] = useState("");

  useEffect(() => {
    const token = sp.get("token");
    if (!token) { setState("error"); setMessage("No verification token in the link."); return; }
    (async () => {
      try {
        const { data } = await api.get(`/auth/verify-email?token=${encodeURIComponent(token)}`);
        setState("ok");
        setMessage(data.message || "Email verified.");
        refresh();
      } catch (err) {
        setState("error");
        setMessage(formatError(err.response?.data?.detail) || "This verification link is invalid or expired.");
      }
    })();
  }, [sp, refresh]);

  return (
    <div className="min-h-screen text-foreground">
      <Navbar />
      <div className="max-w-lg mx-auto px-4 py-20 text-center" data-testid="verify-email-page">
        {state === "loading" && (
          <div className="flex flex-col items-center gap-4" data-testid="verify-loading">
            <Loader2 className="w-10 h-10 text-olive-bright animate-spin" />
            <p className="font-mono uppercase text-sm text-muted-foreground">Verifying your email…</p>
          </div>
        )}
        {state === "ok" && (
          <div className="flex flex-col items-center gap-4" data-testid="verify-success">
            <CheckCircle2 className="w-14 h-14 text-olive-bright" />
            <h1 className="font-heading text-3xl uppercase tracking-tight">Email verified</h1>
            <p className="font-body text-secondary-foreground">{message}</p>
            <div className="flex gap-3 mt-2">
              <Link to="/create" data-testid="verify-create-link" className="bg-olive hover:bg-olive-bright text-white font-heading uppercase px-5 py-2.5 transition-colors">Post a listing</Link>
              <Link to="/feed" className="border border-sniper hover:border-olive text-foreground font-mono uppercase text-sm px-5 py-2.5 transition-colors flex items-center gap-2"><CrosshairIcon className="w-4 h-4" /> Browse feed</Link>
            </div>
          </div>
        )}
        {state === "error" && (
          <div className="flex flex-col items-center gap-4" data-testid="verify-error">
            <XCircle className="w-14 h-14 text-blaze" />
            <h1 className="font-heading text-3xl uppercase tracking-tight">Couldn't verify</h1>
            <p className="font-body text-secondary-foreground">{message}</p>
            <Link to="/create" className="border border-sniper hover:border-olive text-foreground font-mono uppercase text-sm px-5 py-2.5 mt-2 transition-colors">Resend from your listing page</Link>
          </div>
        )}
      </div>
    </div>
  );
}
